import React, { forwardRef, Fragment, useEffect } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { MotionDurationsSeconds, Transitions, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import Portal from '@polestar/component-warehouse-react/components/portal/portal';
import { animate, AnimatePresence, m as motion, useMotionValue, useTransform, } from 'framer-motion';
import { useScrollObserver } from '@polestar/component-warehouse-react/components/scroll-observer/ScrollObserver';
import Section from '@polestar/component-warehouse-react/components/section/Section';
import ScrollProgress from '@polestar/component-warehouse-react/components/scroll-progress/ScrollProgress';
import { InvertedThemeProvider } from '@polestar/component-warehouse-react/components/theme-providers/ThemeProviders';
const getProgressLabel = (value) => `${Math.round(value)}%`;
const ScrollProgressBar = forwardRef(({ children, detached = true, visible = true, progressLabel = getProgressLabel, progress, contentProps, ...props }, ref) => {
    const { showScrollProgressBar, addProgressListener, removeProgressListener } = useScrollObserver();
    const internalProgress = useMotionValue(typeof progress === 'number' ? progress : 0);
    useEffect(() => {
        if (progress) {
            // Update motion value with number
            if (typeof progress === 'number') {
                const controls = animate(internalProgress, progress, {
                    duration: MotionDurationsSeconds.normal,
                });
                return controls.stop;
            }
            else {
                return progress.on('change', (latestProgress) => internalProgress.set(latestProgress));
            }
        }
        const listener = (progress) => {
            internalProgress.set(progress);
        };
        addProgressListener(listener); //
        return () => {
            removeProgressListener(listener);
        };
    }, [addProgressListener, internalProgress, progress, removeProgressListener]);
    const currentProgress = useTransform(internalProgress, [0, 100], [0, 1]);
    const Wrapper = detached ? Portal : Fragment;
    const isVisible = showScrollProgressBar !== undefined ? showScrollProgressBar && visible : visible;
    return (React.createElement(Wrapper, null,
        React.createElement(AnimatePresence, null, isVisible && (React.createElement(Container, { key: "scroll-progress-bar", ...props, ref: ref, detached: detached, initial: {
                opacity: 0,
            }, animate: {
                opacity: 1,
            }, exit: {
                opacity: 0,
            }, transition: Transitions.default },
            React.createElement(StyledScrollProgress, { detached: false, progress: currentProgress, progressLabel: progressLabel }),
            children && (React.createElement(StyledSection, null,
                React.createElement(InnerContent, { ...contentProps },
                    React.createElement(InvertedThemeProvider, null, children)))))))));
});
ScrollProgressBar.displayName = 'ScrollProgressBar';
export default ScrollProgressBar;
const Container = styled(motion.div)(({ detached }) => css(css `
      display: block;
      position: relative;
      width: 100%;
      z-index: 1;
    `, detached &&
    css `
        position: fixed;
        inset-block-end: 0;
        inset-inline-start: 0;
        inset-inline-end: 0;
      `));
Container.displayName = `Container`;
const StyledScrollProgress = styled(ScrollProgress) `
  position: absolute;
  inset-block-start: -${createCustomSpacing(4 / 16)};
`;
const StyledSection = styled(Section)(({ theme }) => css `
    z-index: 1;
    background-color: ${theme.progressBar.background};
    padding-block-start: ${Spacings.medium};
    padding-block-end: ${Spacings.medium};
  `);
const InnerContent = styled.div `
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;
